import { keyValueMirror } from 'store/store-functions';
import { addMessage, clearMessages } from './app';
import { MESSAGE_TYPES } from 'components';

const { VITE_API_DEV_HOST } = process.env;

const baseUrl = VITE_API_DEV_HOST;

export const actions = keyValueMirror(
    'SET_SELECTED_TEAM',
    'GET_TEAM_DATA',
    'GET_TEAM_MODELS',
    'UPDATE_TEAM_USER',
    'DELETE_TEAM_USER',
    'ADD_TEAM_USER',
    'SET_IS_LOADING',
    'SET_USER_IS_LOADING',
    'UPDATE_TEAM_DATA',
    'GET_TEAM_STATISTICS',
);

export const setSelectedTeam = teamId => ({
    type: actions.SET_SELECTED_TEAM,
    payload: teamId,
});

export const setIsLoading = value => ({
    type: actions.SET_IS_LOADING,
    payload: value,
});

export const setTeamUserIsLoading = value => ({
    type: actions.SET_USER_IS_LOADING,
    payload: value,
});

export const getTeamData = (token, teamId) => async dispatch => {
    const getTeamDataUrl = `${VITE_API_DEV_HOST}/teams/${teamId}`;
    const getTeamDataOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    };

    try {
        dispatch(setIsLoading(true));
        const res = await fetch(getTeamDataUrl, getTeamDataOptions);
        if (!res.ok) {
            if (res.status === 404) {
                const message = 'Team not found.';
                const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
                dispatch(addMessage([ { message, config } ]));
            } else {
                const message = 'Unable to retrieve team information.';
                const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
                dispatch(addMessage([ { message, config } ]));
            }
            return;
        }

        const data = await res.json();
        const action = { type: actions.GET_TEAM_DATA, payload: data };
        dispatch(action);
    } catch (err) {
        const message = err.message || 'Error retrieving team data.';
        const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
        dispatch(clearMessages());
        dispatch(addMessage([ { message, config } ]));
    } finally {
        dispatch(setIsLoading(false));

    }
};

/**
 *
 * @param token
 * @param teamId
 * @param userId
 * @param role
 *
 */
export const updateTeamMemberRole = (token, teamId, userId, role) => async dispatch => {
    const url = `${VITE_API_DEV_HOST}/teams/${teamId}/users/${userId}`;
    const options = {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
    };

    try {
        dispatch(setTeamUserIsLoading(userId));
        const res = await fetch(url, options);
        if (!res.ok) {
            const message = `Unable to update role to ${role}`;
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(clearMessages());
            dispatch(addMessage([ { message, config } ]));
        }

        const data = await res.json();
        const action = { type: actions.UPDATE_TEAM_USER, payload: data };
        dispatch(action);
    } catch (err) {
        const message = err.message || 'Error updating member role.';
        const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
        dispatch(clearMessages());
        dispatch(addMessage([ { message, config } ]));
    } finally {
        dispatch(setTeamUserIsLoading(null));
    }

};

export const deleteTeamMember = (token, teamId, userId, onComplete) => async dispatch => {
    const url = `${VITE_API_DEV_HOST}/teams/${teamId}/users/${userId}`;
    const options = {
        method: 'DELETE',
        headers: {
            Authorization: 'Bearer ' + token,
        },
    };

    try {
        const res = await fetch(url, options);
        if (!res.ok) {
            const message = `Unable to remove team member.`;
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(clearMessages());
            dispatch(addMessage([ { message, config } ]));
        }

        const data = await res.json();
        const action = { type: actions.DELETE_TEAM_USER, payload: data };
        dispatch(action);
    } catch (err) {
        const message = err.message || 'Error removing team member.';
        const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
        dispatch(clearMessages());
        dispatch(addMessage([ { message, config } ]));
    } finally {
        onComplete?.();
    }
};


/**
 * Store Action to add a user to a team
 * @param {string} token - the user's AVS access token
 * @param {string} teamId - the team ID the user is to be added to
 * @param {string} userEmail - the user's email used to be added to the team
 * @param {'admin'|'member'} role - the user's role that is being added to the team
 * @returns {Promise.<>}
 */
export const addTeamMember = (token, teamId, userEmail, role) => async dispatch => {
    const url = `${VITE_API_DEV_HOST}/teams/${teamId}/users`;
    const data = {
        users: [
            { email: userEmail, role },
        ],
    };
    const options = {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    };

    try {
        const res = await fetch(url, options);
        if (!res.ok) {
            const message = `Unable to add member to team.`;
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(clearMessages());
            dispatch(addMessage([ { message, config } ]));
            return;
        }

        const data = await res.json();
        if (data.erroredUsers.length != 0) {
            const message = 'An error occurred adding team member.';
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(clearMessages());
            dispatch(addMessage([ { message, config } ]));
            return;
        }

        const action = { type: actions.ADD_TEAM_USER };
        dispatch(action);
        dispatch(getTeamData(token, teamId));

    } catch (err) {
        const message = err.message || 'Error adding team member.';
        const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
        dispatch(clearMessages());
        dispatch(addMessage([ { message, config } ]));
    }
};

/**
 * The data for updating a team.
 * @typedef TeamUpdateData
 * @property {string} displayName - the team's name to display
 * @property {string[]} domains - the list of allowed domains for the team's members
 */

/**
 * Sends a request to update a team's metadata
 * @param {string} token - the user's AVS access token
 * @param {string} teamId - the ID of the team to be updated
 * @param {TeamUpdateData} updateData - data to be update the team with
 * @returns {Promise.<>}
 */
export const updateTeamData = (token, teamId, updateData) => async dispatch => {
    const { displayName, domains } = updateData;
    const url = `${VITE_API_DEV_HOST}/teams/${teamId}`;
    const options = {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            display_name: displayName,
            allowed_domains: domains,
        }),
    };

    try {
        const res = await fetch(url, options);
        if (!res.ok) {
            const message = `Unable to update team data.`;
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(clearMessages());
            dispatch(addMessage([ { message, config } ]));
            return;
        }
        const action = { type: actions.UPDATE_TEAM_DATA };
        dispatch(action);
        dispatch(getTeamData(token, teamId));

    } catch (err) {
        const message = err.message || 'Error updating team data.';
        const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
        dispatch(clearMessages());
        dispatch(addMessage([ { message, config } ]));
    }
};

/**
 * Retrieves a team's statistics from AVS
 * @param {string} token - the user's AVS access token
 * @param {string} teamId - the team ID that the stats are to be retrieved from
 * @returns
 */
export const getTeamStatistics = (token, teamId) => async dispatch => {
    const params = window.location.search;
    const url = `${baseUrl}/teams/${teamId}/statistics${params}`;
    const options = {
        method: 'GET',
        headers: {
            Authorization: 'Bearer ' + token,
        },
    };

    try {
        dispatch(setIsLoading(true));
        const res = await fetch(url, options);
        if (!res.ok) {
            const message = `Unable to retrieve team statistics.`;
            const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
            dispatch(clearMessages());
            dispatch(addMessage([ { message, config } ]));
            return;
        }

        const data = await res.json();
        const action = { type: actions.GET_TEAM_STATISTICS, payload: data };
        dispatch(action);

    } catch (err) {
        const message = err.message || 'Error retrieving team statistics.';
        const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
        dispatch(clearMessages());
        dispatch(addMessage([ { message, config } ]));
    } finally {
        dispatch(setIsLoading(false));
    }
};
